import styled from 'styled-components';

/**
 * Shared styling for the row/kebab context menus (folder + story actions).
 * Follows the design-system context-menu pattern: icon-left items, default
 * hover = `--surface-elevated` (no text-color flip), destructive hover fills
 * with `--destructive`, toast-level elevation.
 */

/** Viewport inset kept between a floating menu and the window edge. */
const MENU_VIEWPORT_PADDING = 8;

/**
 * Clamp a desired top-left position so a `width`×`height` floating menu stays
 * fully within the viewport (with an 8px inset). Shared by the Story Manager
 * row / kebab / breadcrumb / context menus — each passes its own dimensions.
 */
export const clampMenuToViewport = (
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } => {
  if (typeof window === 'undefined') return { x, y };
  const maxX = window.innerWidth - width - MENU_VIEWPORT_PADDING;
  const maxY = window.innerHeight - height - MENU_VIEWPORT_PADDING;
  return {
    x: Math.max(MENU_VIEWPORT_PADDING, Math.min(x, maxX)),
    y: Math.max(MENU_VIEWPORT_PADDING, Math.min(y, maxY)),
  };
};

export const KebabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--spacing-xs);
  margin-left: var(--spacing-sm);
  cursor: pointer;
  color: var(--text-muted);
  border-radius: var(--radius-button);
  transition:
    background 120ms ease,
    color 120ms ease;

  &:hover {
    background: var(--border);
    color: var(--text-primary);
  }
`;

export const ContextMenuSurface = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
  padding: var(--spacing-xs);
  /* Floating menus sit on --background, not --surface — see the design-system
     "Context / Action Menus" container spec. Keeps every menu on one surface
     token whether it reuses these components or hand-rolls its own. */
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--elevation-toast);
  color: var(--foreground);
  z-index: 100001;
`;

export const ContextMenuItem = styled.button<{
  $danger?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $danger, $disabled }) =>
    $disabled
      ? 'var(--text-muted)'
      : $danger
        ? 'var(--destructive)'
        : 'var(--foreground)'};
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
  transition:
    background 120ms ease,
    color 120ms ease;

  /* Normalize every menu icon to 15×15 regardless of its intrinsic size. */
  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  /* Disabled rows are inert — no hover affordance (kept visible + hoverable so
     a native title tooltip can explain why). */
  &:hover {
    background: ${({ $danger, $disabled }) =>
      $disabled
        ? 'transparent'
        : $danger
          ? 'var(--destructive)'
          : 'var(--surface-elevated)'};
    color: ${({ $danger, $disabled }) =>
      $disabled
        ? 'var(--text-muted)'
        : $danger
          ? 'var(--destructive-foreground)'
          : 'var(--foreground)'};
  }
`;

/**
 * PLAT-11979 — a section's label inside a menu that groups its rows. Sections
 * are labelled, never ruled: the design system keeps full-width divider lines
 * out of row menus, so the label (and the gap above it) is the only seam.
 */
export const MenuSectionLabel = styled.div`
  padding: 8px 10px 2px;
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  color: var(--text-muted);
  white-space: nowrap;
  user-select: none;

  &:not(:first-child) {
    margin-top: var(--spacing-xs);
  }
`;

/** Muted facts after a row's label (a code, a state), pushed to the right. */
export const MenuItemMeta = styled.span`
  margin-left: auto;
  padding-left: 12px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  white-space: nowrap;
`;

/** Tells a row it opens a submenu beside it; sits after any `MenuItemMeta`. */
export const SubmenuCaret = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 8px;
  font-size: 15px;
  line-height: 1;
  color: var(--text-muted);

  ${MenuItemMeta} + & {
    margin-left: 0;
    padding-left: 6px;
  }
`;
